import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, Button, Tag, Space, Input, Select, Card, Typography,
  Drawer, Form, Row, Col, Progress, message, Badge, Tooltip,
  Segmented,
} from "antd";
import {
  PlusOutlined, SearchOutlined, ArrowRightOutlined,
  FilterOutlined, TableOutlined, AppstoreOutlined,
} from "@ant-design/icons";
import { proyectosApi } from "../api/client";
import type { Proyecto, RAGEnum, EstadoProyectoEnum, FaseEnum } from "../types";
import { RAG_COLORS } from "../types";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

// ── RAG indicator ─────────────────────────────────────────────────────────────
const RAGBadge = ({ value }: { value: RAGEnum }) => (
  <Badge
    color={RAG_COLORS[value]}
    text={<Text style={{ fontSize: 12 }}>{value}</Text>}
  />
);

const ESTADO_OPTIONS: EstadoProyectoEnum[] = [
  "Scoping", "Estimating", "Approving", "Scheduling",
  "Developing", "UAT", "Deploying", "Finished", "Cancelled",
];
const FASE_OPTIONS: FaseEnum[] = ["F0", "F1", "Triage CAI", "F2", "F3", "F4"];
const RAG_OPTIONS: RAGEnum[] = ["Verde", "Ámbar", "Rojo"];

// ── Project Card (grid view) ──────────────────────────────────────────────────
function ProyectoCard({ proyecto, onClick }: { proyecto: Proyecto; onClick: () => void }) {
  const diasDesdeUltimoUpdate = proyecto.fecha_ultimo_cambio
    ? dayjs().diff(dayjs(proyecto.fecha_ultimo_cambio), "day")
    : null;

  return (
    <Card
      size="small"
      hoverable
      onClick={onClick}
      style={{ borderLeft: `3px solid ${RAG_COLORS[proyecto.estado_rag]}` }}
      bodyStyle={{ padding: "12px 16px" }}
    >
      <Space direction="vertical" size={6} style={{ width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Text code style={{ fontSize: 11 }}>{proyecto.proj_id}</Text>
          <RAGBadge value={proyecto.estado_rag} />
        </div>
        <Text strong style={{ fontSize: 13, lineHeight: 1.3 }} ellipsis={{ tooltip: proyecto.nombre }}>
          {proyecto.nombre}
        </Text>
        <Space size={4} wrap>
          <Tag style={{ fontSize: 11, margin: 0 }}>{proyecto.fase}</Tag>
          <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>{proyecto.estado}</Tag>
          {proyecto.tiene_ia && <Tag color="purple" style={{ fontSize: 11, margin: 0 }}>IA</Tag>}
        </Space>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
            <Text style={{ fontSize: 11, color: "#888" }}>Avance</Text>
            <Text style={{ fontSize: 11 }}>{proyecto.porcentaje_avance}%</Text>
          </div>
          <Progress
            percent={proyecto.porcentaje_avance}
            showInfo={false}
            size="small"
            strokeColor={RAG_COLORS[proyecto.estado_rag]}
            trailColor="#f0f0f0"
          />
        </div>
        {diasDesdeUltimoUpdate !== null && diasDesdeUltimoUpdate > 7 && (
          <Text style={{ fontSize: 11, color: "#faad14" }}>
            Sin actualizar: {diasDesdeUltimoUpdate}d
          </Text>
        )}
      </Space>
    </Card>
  );
}

// ── Create Drawer ─────────────────────────────────────────────────────────────
function CrearProyectoDrawer({
  open, onClose, onSuccess,
}: {
  open: boolean; onClose: () => void; onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      // ── CAPTURA DEL OWNER_ID ────────────────────────────────────────────────
      // Intenta leer el usuario del localStorage (ajusta "user" según tu clave exacta)
      // Si no existe, usa por ahora el UUID de respaldo de tus logs para pruebas
      const storedUser = localStorage.getItem("user");
      const userParsed = storedUser ? JSON.parse(storedUser) : null;
      const ownerId = userParsed?.id || "86ceee81-0e2f-44f1-9774-7bc518edfb1e"; 

      const proyectoPayload = {
        ...values,
        project_owner_id: ownerId, // Inyectamos el ID que pide el backend
        fecha_inicio: values.fecha_inicio || null,         // Saneamos "" a null
        fecha_fin_prevista: values.fecha_fin_prevista || null, // Saneamos "" a null
      };
      // ────────────────────────────────────────────────────────────────────────

      await proyectosApi.crear(proyectoPayload);
      // await proyectosApi.crear(values);
      message.success("Proyecto creado correctamente");
      form.resetFields();
      onSuccess();
      onClose();
    } catch (err: any) {
      if (err?.response?.data?.detail) message.error(err.response.data.detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title="Nuevo proyecto"
      width={560}
      open={open}
      onClose={onClose}
      footer={
        <Space style={{ float: "right" }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit} style={{ background: "#1a1f5e" }}>
            Crear proyecto
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" requiredMark="optional"
        initialValues={{ fase: "F0", estado: "Scoping", prioridad: "Media", linea: "Línea B" }}>
        <Form.Item name="nombre" label="Nombre del proyecto" rules={[{ required: true }]}>
          <Input placeholder="Nombre descriptivo del proyecto..." />
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
              <Select>
                {["Proyecto cliente", "Proyecto interno", "Innovación interna", "Assessment", "Quick Win escalado", "FAB corporativo"].map(
                  (t) => <Option key={t} value={t}>{t}</Option>
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="linea" label="Línea" rules={[{ required: true }]}>
              <Select>
                <Option value="Línea A">Línea A</Option>
                <Option value="Línea B">Línea B</Option>
                <Option value="Línea C">Línea C</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="fase" label="Fase">
              <Select>{FASE_OPTIONS.map((f) => <Option key={f} value={f}>{f}</Option>)}</Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="estado" label="Estado">
              <Select>{ESTADO_OPTIONS.map((e) => <Option key={e} value={e}>{e}</Option>)}</Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="prioridad" label="Prioridad">
              <Select>
                {["Urgente", "Alta", "Media", "Baja"].map((p) => <Option key={p} value={p}>{p}</Option>)}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="fecha_inicio" label="Fecha inicio">
              <Input type="date" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="fecha_fin_prevista" label="Fecha fin prevista">
              <Input type="date" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="cliente_area" label="Cliente / área">
          <Input placeholder="Nombre del cliente o área interna" />
        </Form.Item>
        <Form.Item name="notas_proximos_pasos" label="Próximos pasos">
          <Input.TextArea rows={3} placeholder="Acciones inmediatas previstas..." />
        </Form.Item>
      </Form>
    </Drawer>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ProyectosPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<Proyecto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [view, setView] = useState<"tabla" | "grid">("tabla");
  const [search, setSearch] = useState("");
  const [ragFiltro, setRagFiltro] = useState<RAGEnum | undefined>();
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoProyectoEnum | undefined>();
  const [page, setPage] = useState(1);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await proyectosApi.listar({
        page, page_size: 20,
        estado_rag: ragFiltro,
        estado: estadoFiltro,
      });
      setData(res.data.items);
      setTotal(res.data.total);
    } catch {
      message.error("Error cargando proyectos");
    } finally {
      setLoading(false);
    }
  }, [page, ragFiltro, estadoFiltro]);

  useEffect(() => { cargar(); }, [cargar]);

  const filtered = data.filter((p) =>
    !search ||
    p.proj_id.toLowerCase().includes(search.toLowerCase()) ||
    p.nombre.toLowerCase().includes(search.toLowerCase()) ||
    p.cliente_area?.toLowerCase().includes(search.toLowerCase())
  );

  const columns = [
    {
      title: "ID",
      dataIndex: "proj_id",
      width: 100,
      render: (v: string) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: "Nombre",
      dataIndex: "nombre",
      ellipsis: true,
      render: (v: string) => <Text style={{ fontSize: 13 }}>{v}</Text>,
    },
    { title: "Fase", dataIndex: "fase", width: 100, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: "Estado",
      dataIndex: "estado",
      width: 120,
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: "RAG",
      dataIndex: "estado_rag",
      width: 90,
      render: (v: RAGEnum) => <RAGBadge value={v} />,
    },
    {
      title: "Avance",
      dataIndex: "porcentaje_avance",
      width: 110,
      render: (v: number, r: Proyecto) => (
        <Progress
          percent={v} size="small" showInfo={false}
          strokeColor={RAG_COLORS[r.estado_rag]} trailColor="#f0f0f0"
        />
      ),
    },
    {
      title: "IA",
      dataIndex: "tiene_ia",
      width: 50,
      render: (v: boolean) => v ? <Tag color="purple" style={{ fontSize: 11 }}>IA</Tag> : null,
    },
    {
      title: "Fin previsto",
      dataIndex: "fecha_fin_prevista",
      width: 110,
      render: (v: string) => {
        if (!v) return <Text type="secondary">—</Text>;
        const dias = dayjs(v).diff(dayjs(), "day");
        const color = dias < 0 ? "#ff4d4f" : dias < 14 ? "#faad14" : "#666";
        return <Text style={{ fontSize: 12, color }}>{dayjs(v).format("DD/MM/YYYY")}</Text>;
      },
    },
    {
      title: "",
      width: 70,
      render: (_: unknown, r: Proyecto) => (
        <Button type="link" size="small" icon={<ArrowRightOutlined />}
          onClick={() => navigate(`/proyectos/${r.id}`)}>Ver</Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Space style={{ justifyContent: "space-between", width: "100%" }}>
          <div>
            <Title level={4} style={{ margin: 0, color: "#1a1f5e" }}>Proyectos Activos</Title>
            <Text type="secondary" style={{ fontSize: 13 }}>Portfolio E2E IT + TD · Total: {total}</Text>
          </div>
          <Space>
            <Segmented
              value={view}
              onChange={(v) => setView(v as any)}
              options={[
                { value: "tabla", icon: <TableOutlined /> },
                { value: "grid", icon: <AppstoreOutlined /> },
              ]}
            />
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => setDrawerOpen(true)} style={{ background: "#1a1f5e" }}>
              Nuevo proyecto
            </Button>
          </Space>
        </Space>
      </div>

      {/* Filtros */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle">
          <Col flex={1}>
            <Input prefix={<SearchOutlined />} placeholder="Buscar por ID, nombre, cliente..."
              value={search} onChange={(e) => setSearch(e.target.value)} allowClear />
          </Col>
          <Col>
            <Select style={{ width: 140 }} placeholder="RAG" allowClear
              value={ragFiltro} onChange={(v) => { setRagFiltro(v); setPage(1); }}>
              {RAG_OPTIONS.map((r) => (
                <Option key={r} value={r}>
                  <Badge color={RAG_COLORS[r]} text={r} />
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Select style={{ width: 150 }} placeholder="Estado" allowClear
              value={estadoFiltro} onChange={(v) => { setEstadoFiltro(v); setPage(1); }}>
              {ESTADO_OPTIONS.map((e) => <Option key={e} value={e}>{e}</Option>)}
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Vista tabla / grid */}
      {view === "tabla" ? (
        <Card size="small">
          <Table
            dataSource={filtered} columns={columns} rowKey="id"
            loading={loading} size="small"
            pagination={{ current: page, pageSize: 20, total, onChange: setPage,
              showSizeChanger: false, showTotal: (t) => `${t} proyectos` }}
            onRow={(r) => ({ style: { cursor: "pointer" }, onClick: () => navigate(`/proyectos/${r.id}`) })}
          />
        </Card>
      ) : (
        <Row gutter={[12, 12]}>
          {loading ? [...Array(8)].map((_, i) => (
            <Col key={i} xs={24} sm={12} md={8} xl={6}>
              <Card size="small" loading />
            </Col>
          )) : filtered.map((p) => (
            <Col key={p.id} xs={24} sm={12} md={8} xl={6}>
              <ProyectoCard proyecto={p} onClick={() => navigate(`/proyectos/${p.id}`)} />
            </Col>
          ))}
        </Row>
      )}

      <CrearProyectoDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} onSuccess={cargar} />
    </div>
  );
}
